import type {
    ApiActiveSchedule,
    ApiActivityResolveResponse,
    ApiScheduleActivity,
    ApiScheduleProject,
    ApiScheduleProjectsResponse,
    ApiScheduleRevision,
    ApiWBSChildrenResponse,
    ApiWBSNode,
    ApiWBSTreeResponse,
} from "../../shared/interfaces";
import type { ProjectTransport } from "./transport";
import {
    createProjectRowDropReporter,
    type ProjectRowDropReporter,
    requireArray,
    requireBoolean,
    requireFiniteNumber,
    requireNonNegativeInteger,
    requireNullableWireString,
    requireRecord,
    requireString,
    requireWireString,
    requireWireStringArray,
    validateResponseListRows,
} from "./validation";

function validateActivity(value: unknown, endpoint: string, field: string): ApiScheduleActivity {
    const row = requireRecord(value, endpoint, field);
    requireString(row.id, endpoint, `${field}.id`);
    requireWireString(row.taskCode, endpoint, `${field}.taskCode`);
    requireWireString(row.taskName, endpoint, `${field}.taskName`);
    requireWireString(row.status, endpoint, `${field}.status`);
    requireFiniteNumber(row.durationDays, endpoint, `${field}.durationDays`);
    requireFiniteNumber(row.totalFloatDays, endpoint, `${field}.totalFloatDays`);
    requireFiniteNumber(row.percentComplete, endpoint, `${field}.percentComplete`);
    requireBoolean(row.isCriticalPath, endpoint, `${field}.isCriticalPath`);
    requireNullableWireString(row.targetStart, endpoint, `${field}.targetStart`);
    requireNullableWireString(row.targetEnd, endpoint, `${field}.targetEnd`);
    requireWireStringArray(row.wbsPath, endpoint, `${field}.wbsPath`);
    return row as unknown as ApiScheduleActivity;
}

function validateActivities(
    value: unknown,
    endpoint: string,
    field: string,
    reporter: ProjectRowDropReporter = createProjectRowDropReporter(),
): ApiScheduleActivity[] {
    const rows = requireArray(value, endpoint, field);
    return validateResponseListRows(
        rows,
        endpoint,
        "schedule activity",
        (value, field) => validateActivity(value, endpoint, field),
        reporter,
        field,
    );
}

function validateProject(value: unknown, endpoint: string, field: string): ApiScheduleProject {
    const row = requireRecord(value, endpoint, field);
    requireString(row.id, endpoint, `${field}.id`);
    requireString(row.projectId, endpoint, `${field}.projectId`);
    requireWireString(row.projectName, endpoint, `${field}.projectName`);
    requireNonNegativeInteger(row.activityCount, endpoint, `${field}.activityCount`);
    requireNonNegativeInteger(row.wbsCount, endpoint, `${field}.wbsCount`);
    requireNullableWireString(row.earliestStart, endpoint, `${field}.earliestStart`);
    requireNullableWireString(row.latestEnd, endpoint, `${field}.latestEnd`);
    requireWireStringArray(row.calendars, endpoint, `${field}.calendars`);
    return row as unknown as ApiScheduleProject;
}

function validateProjects(
    value: unknown,
    endpoint: string,
    field: string,
    reporter: ProjectRowDropReporter = createProjectRowDropReporter(),
): ApiScheduleProject[] {
    const rows = requireArray(value, endpoint, field);
    return validateResponseListRows(
        rows,
        endpoint,
        "schedule project",
        (value, field) => validateProject(value, endpoint, field),
        reporter,
        field,
    );
}

function validateWbsNode(value: unknown, endpoint: string, field: string): ApiWBSNode {
    const row = requireRecord(value, endpoint, field);
    requireString(row.id, endpoint, `${field}.id`);
    requireWireString(row.name, endpoint, `${field}.name`);
    requireWireString(row.taskCode, endpoint, `${field}.taskCode`);
    requireNonNegativeInteger(row.activityCount, endpoint, `${field}.activityCount`);
    requireNonNegativeInteger(row.childCount, endpoint, `${field}.childCount`);
    requireBoolean(row.hasChildren, endpoint, `${field}.hasChildren`);
    requireNonNegativeInteger(row.depth, endpoint, `${field}.depth`);
    return row as unknown as ApiWBSNode;
}

function validateWbsNodes(
    value: unknown,
    endpoint: string,
    field: string,
    reporter: ProjectRowDropReporter = createProjectRowDropReporter(),
): ApiWBSNode[] {
    const rows = requireArray(value, endpoint, field);
    return validateResponseListRows(
        rows,
        endpoint,
        "WBS node",
        (value, field) => validateWbsNode(value, endpoint, field),
        reporter,
        field,
    );
}

function validateWbsChildren(
    value: unknown,
    endpoint: string,
    field: string,
    reporter: ProjectRowDropReporter = createProjectRowDropReporter(),
): ApiWBSChildrenResponse {
    const payload = requireRecord(value, endpoint, field);
    const wbsNodes = validateWbsNodes(payload.wbsNodes, endpoint, `${field}.wbsNodes`, reporter);
    const activities = validateActivities(
        payload.activities,
        endpoint,
        `${field}.activities`,
        reporter,
    );
    return wbsNodes === payload.wbsNodes && activities === payload.activities
        ? (payload as unknown as ApiWBSChildrenResponse)
        : ({ ...payload, wbsNodes, activities } as unknown as ApiWBSChildrenResponse);
}

interface ScheduleSearchOptions {
    criticalOnly?: boolean;
    limit?: number;
    projectId?: string;
}

interface ScheduleSearchResult {
    activities: ApiScheduleActivity[];
    totalMatches: number;
    query: string;
}

/** Current schedule route omits the UI-only `isLoaded` decoration. */
type ProjectScheduleProject = Omit<ApiScheduleProject, "isLoaded"> & {
    isLoaded?: boolean;
};

export type ProjectScheduleProjectsResponse = Omit<ApiScheduleProjectsResponse, "projects"> & {
    projects: ProjectScheduleProject[];
};

export type ProjectActiveSchedule = Omit<ApiActiveSchedule, "projects"> & {
    projects: ProjectScheduleProject[];
};

export interface ProjectScheduleApi {
    getScheduleActivities(fileId: string, signal?: AbortSignal): Promise<ApiScheduleActivity[]>;
    searchScheduleActivities(
        fileId: string,
        query: string,
        options?: ScheduleSearchOptions,
        signal?: AbortSignal,
    ): Promise<ScheduleSearchResult>;
    getScheduleProjects(
        fileId: string,
        signal?: AbortSignal,
    ): Promise<ProjectScheduleProjectsResponse>;
    getScheduleWBSTree(
        fileId: string,
        projectId?: string,
        signal?: AbortSignal,
    ): Promise<ApiWBSTreeResponse>;
    getWBSChildren(wbsId: string, signal?: AbortSignal): Promise<ApiWBSChildrenResponse>;
    resolveScheduleActivity(
        fileId: string,
        taskCode: string,
        options?: { projectId?: string },
        signal?: AbortSignal,
    ): Promise<ApiActivityResolveResponse>;
    getActiveSchedule(signal?: AbortSignal): Promise<{ schedule: ProjectActiveSchedule | null }>;
    getScheduleRevisions(signal?: AbortSignal): Promise<{ revisions: ApiScheduleRevision[] }>;
}

export function createScheduleApi(transport: ProjectTransport): ProjectScheduleApi {
    return {
        async getScheduleActivities(fileId, signal) {
            const path = `/api/ai/schedule/file/${fileId}/activities`;
            const response = await transport.request<unknown>({ method: "GET", path, signal });
            const payload = requireRecord(response, path, "response");
            return validateActivities(payload.activities, path, "activities");
        },

        async searchScheduleActivities(fileId, query, options, signal) {
            const params = new URLSearchParams({ query });
            if (options?.criticalOnly) params.set("critical_only", "true");
            if (options?.limit) params.set("limit", options.limit.toString());
            if (options?.projectId) params.set("project_id", options.projectId);
            const path = `/api/ai/schedule/file/${fileId}/activities/search?${params}`;
            const response = await transport.request<unknown>({ method: "GET", path, signal });
            const payload = requireRecord(response, path, "response");
            const activities = validateActivities(payload.activities, path, "activities");
            requireNonNegativeInteger(payload.totalMatches, path, "totalMatches");
            requireWireString(payload.query, path, "query");
            return activities === payload.activities
                ? (payload as unknown as ScheduleSearchResult)
                : ({ ...payload, activities } as unknown as ScheduleSearchResult);
        },

        async getScheduleProjects(fileId, signal) {
            const path = `/api/ai/schedule/file/${fileId}/projects`;
            const response = await transport.request<unknown>({ method: "GET", path, signal });
            const payload = requireRecord(response, path, "response");
            const projects = validateProjects(payload.projects, path, "projects");
            requireNonNegativeInteger(payload.totalProjects, path, "totalProjects");
            return projects === payload.projects
                ? (payload as unknown as ProjectScheduleProjectsResponse)
                : ({ ...payload, projects } as unknown as ProjectScheduleProjectsResponse);
        },

        async getScheduleWBSTree(fileId, projectId, signal) {
            const suffix = projectId
                ? `?${new URLSearchParams({ project_id: projectId }).toString()}`
                : "";
            const path = `/api/ai/schedule/file/${fileId}/wbs${suffix}`;
            const response = await transport.request<unknown>({ method: "GET", path, signal });
            const payload = requireRecord(response, path, "response");
            const nodes = validateWbsNodes(payload.nodes, path, "nodes");
            requireNonNegativeInteger(payload.totalActivities, path, "totalActivities");
            return nodes === payload.nodes
                ? (payload as unknown as ApiWBSTreeResponse)
                : ({ ...payload, nodes } as unknown as ApiWBSTreeResponse);
        },

        async getWBSChildren(wbsId, signal) {
            const path = `/api/ai/schedule/wbs/${wbsId}/children`;
            const response = await transport.request<unknown>({ method: "GET", path, signal });
            return validateWbsChildren(response, path, "response");
        },

        async resolveScheduleActivity(fileId, taskCode, options, signal) {
            const params = new URLSearchParams({ task_code: taskCode });
            if (options?.projectId) params.set("project_id", options.projectId);
            const path = `/api/ai/schedule/file/${fileId}/activity/resolve?${params.toString()}`;
            const response = await transport.request<unknown>({ method: "GET", path, signal });
            const payload = requireRecord(response, path, "response");
            const ancestorRows = requireArray(payload.ancestorPath, path, "ancestorPath");
            const reporter = createProjectRowDropReporter();
            const ancestorPath = validateResponseListRows(
                ancestorRows,
                path,
                "WBS ancestor",
                (value, field) => {
                    const ancestor = requireRecord(value, path, field);
                    requireString(ancestor.id, path, `${field}.id`);
                    requireWireString(ancestor.name, path, `${field}.name`);
                    const children = validateWbsChildren(
                        ancestor.children,
                        path,
                        `${field}.children`,
                        reporter,
                    );
                    return children === ancestor.children ? ancestor : { ...ancestor, children };
                },
                reporter,
                "ancestorPath",
            );
            validateActivity(payload.activity, path, "activity");
            return ancestorPath === ancestorRows
                ? (payload as unknown as ApiActivityResolveResponse)
                : ({ ...payload, ancestorPath } as unknown as ApiActivityResolveResponse);
        },

        async getActiveSchedule(signal) {
            const path = "/api/ai/schedule/active";
            const response = await transport.request<unknown>({ method: "GET", path, signal });
            const payload = requireRecord(response, path, "response");
            if (payload.schedule === null) {
                return payload as unknown as { schedule: ProjectActiveSchedule | null };
            }
            const schedule = requireRecord(payload.schedule, path, "schedule");
            requireString(schedule.id, path, "schedule.id");
            requireString(schedule.document_type, path, "schedule.document_type");
            requireWireString(schedule.title, path, "schedule.title");
            requireBoolean(schedule.is_active, path, "schedule.is_active");
            requireNullableWireString(schedule.created_at, path, "schedule.created_at");
            const projects = validateProjects(schedule.projects, path, "schedule.projects");
            if (projects === schedule.projects) {
                return payload as unknown as { schedule: ProjectActiveSchedule | null };
            }
            return {
                ...payload,
                schedule: { ...schedule, projects },
            } as unknown as { schedule: ProjectActiveSchedule | null };
        },

        async getScheduleRevisions(signal) {
            const path = "/api/ai/schedule/revisions";
            const response = await transport.request<unknown>({ method: "GET", path, signal });
            const payload = requireRecord(response, path, "response");
            const rows = requireArray(payload.revisions, path, "revisions");
            const revisions = validateResponseListRows(
                rows,
                path,
                "schedule revision",
                (value, field) => {
                    const revision = requireRecord(value, path, field);
                    requireString(revision.id, path, `${field}.id`);
                    requireWireString(revision.title, path, `${field}.title`);
                    requireBoolean(revision.isActive, path, `${field}.isActive`);
                    requireNullableWireString(revision.createdAt, path, `${field}.createdAt`);
                    requireNonNegativeInteger(revision.projectCount, path, `${field}.projectCount`);
                    requireNonNegativeInteger(
                        revision.activityCount,
                        path,
                        `${field}.activityCount`,
                    );
                    return revision as unknown as ApiScheduleRevision;
                },
                undefined,
                "revisions",
            );
            return revisions === rows
                ? (payload as unknown as { revisions: ApiScheduleRevision[] })
                : ({ ...payload, revisions } as unknown as { revisions: ApiScheduleRevision[] });
        },
    };
}
